import React, {useState, useRef, useEffect} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {Translation as T} from "@awe-web/shared/lib";
import {ScreenSectionHeading} from "../../components/screen.js";
import {Row, Col} from "../../components/grid.js";
import {Cms} from "../../routing/routes.js";
import {getStrPromotionCards, getLtrPromotionCards} from "../store.js";

import "./promotion.scss";

const HEADING_B2C = "FSD0000292";
const HEADING_B2B = "ALG0000250";

function shouldEnter(elem) {
  if (!elem) {
    return false;
  }
  const boundingBox = elem.getBoundingClientRect();
  return boundingBox.top <= (window.innerHeight || document.documentElement.clientHeight);
}

function PromotionCardLink({children, className, pageName, link}) {
  return pageName
    ? <Link className={className} to={Cms.location({pageName})}>{children}</Link>
    : <a className={className} href={link} target="_blank" rel="noopener noreferrer">{children}</a>;
}

PromotionCardLink.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  pageName: PropTypes.string,
  link: PropTypes.string
};

function PromotionCard({card: {heading, text, pageName, link, image} = {}}) {
  const [isVisible, setIsVisible] = useState();
  const elementRef = useRef();

  useEffect(() => {
    if (shouldEnter(elementRef.current)) {
      setIsVisible(true);
    }
    else {
      const onScroll = () => {
        if (shouldEnter(elementRef.current)) {
          setIsVisible(true);
          window.removeEventListener("scroll", onScroll, false);
        }
      };

      window.addEventListener("scroll", onScroll, false);

      return () => {
        window.removeEventListener("scroll", onScroll, false);
      };
    }
  }, []);

  const className = classnames("promotion-card promotion-card-list__promotion-card", {
    "promotion-card--visible": isVisible
  });

  return (
    <Col ref={elementRef} tablet={{cols: 4}} desktop={{cols: 4}} className={className}>
      <PromotionCardLink className="promotion-card__container" pageName={pageName} link={link}>
        <div className="promotion-card__image-wrapper">
          <div
            className="promotion-card__image-wrapper__image"
            style={{backgroundImage: `url("${image}")`}}
          />
        </div>
        <div className="promotion-card__body">
          <div className="promotion-card__heading">{heading}</div>
          <div className="promotion-card__text">{text}</div>
        </div>
      </PromotionCardLink>
    </Col>
  );
}

PromotionCard.propTypes = {
  card: PropTypes.shape({
    heading: PropTypes.string,
    text: PropTypes.string,
    pageName: PropTypes.string,
    link: PropTypes.string,
    image: PropTypes.string
  })
};

function PromotionCardList({cards = []}) {
  return (
    <Row className="promotion-card-list">
      {cards.map(card => (
        <PromotionCard key={card.id} card={card}/>
      ))}
    </Row>
  );
}
PromotionCardList.propTypes = {
  cards: PropTypes.arrayOf(PromotionCard.propTypes.card)
};

function BasePromotion({headingCode, promotionCards}) {
  if (!Array.isArray(promotionCards) || promotionCards.length === 0) {
    return null;
  }

  return (
    <aside className="promotion">
      <ScreenSectionHeading><T code={headingCode} /></ScreenSectionHeading>
      <PromotionCardList cards={promotionCards} />
    </aside>
  );
}

BasePromotion.propTypes = {
  headingCode: PropTypes.string,
  promotionCards: PromotionCardList.propTypes.cards
};

export function B2cPromotion() {
  const promotionCards = useSelector((state) => getStrPromotionCards(state));
  
  return (
    <BasePromotion headingCode={HEADING_B2C} promotionCards={promotionCards.toJS()}/>
  );
}

export function B2bPromotion() {
  const promotionCards = useSelector((state) => getLtrPromotionCards(state));
  
  return (
    <BasePromotion headingCode={HEADING_B2B} promotionCards={promotionCards.toJS()}/>
  );
}
